import { ATTR_SERVICE_NAME } from "@opentelemetry/semantic-conventions";
import { detectResourcesSync, Resource } from "@opentelemetry/resources";
import { OTLPTraceExporter } from "@opentelemetry/exporter-trace-otlp-http";
import { WebTracerProvider, BatchSpanProcessor } from "@opentelemetry/sdk-trace-web";
import { ZoneContextManager } from "@opentelemetry/context-zone-peer-dep";
import {
  CompositePropagator,
  W3CBaggagePropagator,
  W3CTraceContextPropagator,
} from "@opentelemetry/core";
import { browserDetector } from "@opentelemetry/opentelemetry-browser-detector";
import { registerInstrumentations } from "@opentelemetry/instrumentation";
import { getWebAutoInstrumentations } from "@opentelemetry/auto-instrumentations-web";
import { XMLHttpRequestInstrumentation } from "@opentelemetry/instrumentation-xml-http-request";
import { FetchInstrumentation } from "@opentelemetry/instrumentation-fetch";
import { WebVitalsInstrumentation } from "./web-vitals";
import { environment } from "./environments/environment";
import { BaggageSpanProcessor } from "@weps/shared-http";

const ATTR_SERVICE_NAMESPACE: string = "service.namespace";

const application: Resource = new Resource({
  // Currently not required to handle it like this BUT we will need it in the future for external personal wealth inventory work; remove comment upon external personal wealth inventory implementation
  [ATTR_SERVICE_NAME]: environment.internal
    ? "weps-estate-profile-frontend-internal"
    : "weps-estate-profile-frontend-external",
  [ATTR_SERVICE_NAMESPACE]: "WAH",
});

const web = location.href.match(/\.web([et])?\.vanguard\.com/);
const isLocal = (matches: RegExpMatchArray | null): matches is null => matches === null;
const sysLevelSuffix = isLocal(web) ? "e" : web[1] || "p";
const url = `https://gto-ocp.icproxy.sec${sysLevelSuffix}.c1.vanguard.com/v1`;

function registerTracesProvider(application: Resource, url: string): void {
  const resources = detectResourcesSync({ detectors: [browserDetector] });
  const resource = resources.merge(application);
  const provider: WebTracerProvider = new WebTracerProvider({ resource });
  const exporter: OTLPTraceExporter = new OTLPTraceExporter({ url });

  provider.addSpanProcessor(new BatchSpanProcessor(exporter));
  provider.addSpanProcessor(new BaggageSpanProcessor());

  const contextManager: ZoneContextManager = new ZoneContextManager();

  const compositePropagator = new CompositePropagator({
    propagators: [new W3CTraceContextPropagator(), new W3CBaggagePropagator()],
  });

  provider.register({ contextManager, propagator: compositePropagator });
  // Note: provider.register() already sets the global propagator, no need to set it again
}

export const explicitlyAllowedBackendPatterns: RegExp[] = [
  /https:\/\/wealth-management\.retailadvice1[etp]\.c1\.vanguard\.com\/api\/weps-profile\/subgraph\/aan\/graphql/i,
  /https:\/\/wealth-management\.retailadvice1[etp]\.c1\.vanguard\.com\/api\/weps-profile\/subgraph\/ago\/graphql/i,
  /https:\/\/wealth-management\.retailadvice1[etp]\.c1\.vanguard\.com\/api\/estate-plan\/tracker\/status/i,
  /https:\/\/wealth-management\.retailadvice1[etp]\.c1\.vanguard\.com\/api\/estate-plan\/tracker/i,
  /https:\/\/apii\.ecs\.us-east-1\.ret[etp]\.c1\.vanguard\.com\/iiz-investor-profile-base-webapp\/InvestorProfile/i,
  /https:\/\/gatekeeper-service(-test)?\.vanguard\.com\/gat\/toggles\/VEX\?featureIds=.+&env=.+/i,
  /https:\/\/rgh-rglobal-header-ecs-webservice-current-internal\.ecs\.ret[etp]\.c1\.vanguard\.com\/client-data\?ecg=true/i,
  /https:\/\/wealth-management\.retailadvice1[etp]\.c1\.vanguard\.com\/api\/weps-profile\/get-profile\/graphql/i,
  /https:\/\/wealth-management\.retailadvice1[etp]\.c1\.vanguard\.com\/api\/vex\/status/i,
  /https:\/\/wealth-management\.retailadvice1[etp]\.c1\.vanguard\.com\/api\/vex\/estate-profile/i,
  /https:\/\/app\.(edge\.just-vanilla|staging\.just-vanilla|justvanilla)\.com\/sso\/vanguard\/start/i,
  /api\.ecs\.us-east-1\.rete\.c1\.vanguard\.com/i,
  /\/wah-wah-proxy\//i,
  /oauthi\.opse\.c1\.vanguard\.com/i,
];

export function doesApiAllowCorsFromMyDomain(): boolean {
  return location.href.match(/\.vanguard\.com/) !== null;
}

/**
 * Check if a URL should receive OpenTelemetry trace headers
 * Used by HTTP interceptor to prevent header leakage to external APIs
 */
export function isUrlAllowedForTracing(url: string): boolean {
  if (!doesApiAllowCorsFromMyDomain()) {
    return false;
  }
  return explicitlyAllowedBackendPatterns.some((pattern) => pattern.test(url));
}

export function initializeTracing(): void {
  const productionLike: boolean = sysLevelSuffix !== "e";
  const bypassSampling: Resource = new Resource({ "vg.otel.sampling": "bypass" });
  // Fix: Conditionally merge resources to avoid merge(null) error
  const resource = productionLike ? application.merge(bypassSampling) : application;

  registerTracesProvider(resource, `${url}/traces`);

  // Explicit fetch instrumentation with CORS configuration
  // Note: BaggageSpanProcessor handles adding baggage attributes to ALL spans
  const fetchInstrumentation = new FetchInstrumentation({
    propagateTraceHeaderCorsUrls: doesApiAllowCorsFromMyDomain()
      ? explicitlyAllowedBackendPatterns
      : undefined,
  });

  const webVitalsInstrumentation = new WebVitalsInstrumentation("wah", "1", {});

  // Explicit XMLHttpRequest instrumentation with CORS configuration
  const xmlHttpRequestInstrumentation = new XMLHttpRequestInstrumentation({
    propagateTraceHeaderCorsUrls: doesApiAllowCorsFromMyDomain()
      ? explicitlyAllowedBackendPatterns
      : undefined,
  });

  // Register all instrumentations
  // Note: BaggageSpanProcessor handles adding baggage attributes to ALL spans
  // The explicit fetch/XMLHttpRequest configurations above only control CORS header propagation
  const instrumentations = [
    fetchInstrumentation,
    webVitalsInstrumentation,
    xmlHttpRequestInstrumentation,
    getWebAutoInstrumentations(),
  ];

  registerInstrumentations({ instrumentations });
}
